import { useReactiveVar } from '@apollo/client'
import { ExpressCheckoutElement } from '@stripe/react-stripe-js'
import { endOfMonth, isAfter } from 'date-fns'
import { Formik, FormikErrors } from 'formik'
import { toUpper } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { basketItemsToOrderItems } from '@src/components/CheckoutModal/Checkout/utils/basketItemsToOrderItems'
import { CheckoutButtonWrapperMobile } from '@src/components/CheckoutModal/CheckoutButtonWrapperMobile'
import {
  CheckoutButton,
  FlexGrowForm,
  FlexGrowScrollContainer,
} from '@src/components/CheckoutModal/FormElements.styles'
import { ErrorPage } from '@src/components/Errors/ErrorPage'
import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'
import { StripeElementsProvider } from '@src/components/Stripe/StripeElementsWrapper'
import { Totals } from '@src/components/Totals/Totals'
import {
  CardPartial,
  DetailedPaymentMethod,
  LocationType,
  NarrowFulfilmentMethodInputType,
} from '@src/graphql-types'
import { ExtendedCurrentFulfilment } from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/extendData/types'
import {
  OutletFulfilmentStateType,
  useOutletFulfilment,
} from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import {
  SharedAddress,
  useCustomerDetailsAndAddressesQuery,
} from '@src/hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/useCustomerDetailsAndAddressesQuery'
import { usePaymentCardsQuery } from '@src/hooks/sharedQueries/usePaymentCards/usePaymentCards'
import { useBasketItems } from '@src/hooks/useBasketItems/useBasketItems'
import { useBreakpoint } from '@src/hooks/useBreakpoint'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { useSafeArea } from '@src/hooks/useSafeArea'
import { useBasketTotalsContext } from '@src/hooks/useTotals/BasketTotalsContext'
import {
  DetailedPaymentMethodWithCard,
  paymentMethodVar,
} from '@src/hooks/useTotals/utils/paymentMethodReactiveVar'

import { getCreateOrderFulfilmentInput } from './getCreateOrderFulfilmentInput'
import { PaymentMethods } from './PaymentMethods/PaymentMethods'

import { ConfirmAddressModal } from '../ConfirmAddressPopover/ConfirmAddress.modal'
import { useCreateOrderMutation } from '../mutations/useCreateOrderMutation/useCreateOrderMutation'
import { OutletClosedAlert } from '../OutletClosedAlert/OutletClosedAlert'
/**
 * Determines the default payment method based on available cards and fulfillment options.
 * Priority: Valid cards > Cash (if only option) > Add new card
 */
const getDefaultPaymentMethod = (
  paymentCards: CardPartial[],
  isExpired: (card: CardPartial) => boolean,
  currentFulfilment: ExtendedCurrentFulfilment
): DetailedPaymentMethodWithCard => {
  // Get valid (non-expired) cards
  const validCards = paymentCards.filter(card => !isExpired(card))

  // If only cash is available, return cash
  if (
    currentFulfilment.availablePaymentMethods.length === 1 &&
    currentFulfilment.availablePaymentMethods[0] === DetailedPaymentMethod.CASH
  ) {
    return {
      detailedPaymentMethod: DetailedPaymentMethod.CASH,
    }
  }

  // If we have valid cards, return the first one
  if (validCards.length > 0) {
    const firstValidCard = validCards[0]!
    return {
      detailedPaymentMethod: DetailedPaymentMethod.CARD,
      cardPaymentToken: firstValidCard.id,
    }
  }

  // Default to manual confirmation (add new card)
  return {
    detailedPaymentMethod: DetailedPaymentMethod.MANUAL_CONFIRMATION_CARD,
    manualSelection: false,
  }
}

/**
 * Handles payment method updates when cards change (added/removed/expired).
 * Preserves current selection if still valid, otherwise falls back to default logic.
 */
const getNextValidPaymentMethod = (
  paymentCards: CardPartial[],
  isExpired: (card: CardPartial) => boolean,
  currentFulfilment: ExtendedCurrentFulfilment,
  currentPaymentMethod: DetailedPaymentMethodWithCard
): DetailedPaymentMethodWithCard => {
  const validCards = paymentCards.filter(card => !isExpired(card))

  // If current method is a card, try to find the next valid card
  if (
    currentPaymentMethod.detailedPaymentMethod === DetailedPaymentMethod.CARD
  ) {
    const currentCardIndex = validCards.findIndex(
      card => card.id === currentPaymentMethod.cardPaymentToken
    )

    // If current card is still valid, keep it
    if (currentCardIndex !== -1) {
      return currentPaymentMethod
    }

    // Current card is no longer valid, select the first available valid card
    if (validCards.length > 0) {
      return {
        detailedPaymentMethod: DetailedPaymentMethod.CARD,
        cardPaymentToken: validCards[0]!.id,
      }
    }
  }

  // Fall back to default selection logic
  return getDefaultPaymentMethod(paymentCards, isExpired, currentFulfilment)
}

export const PaymentStep: React.FC = () => {
  const { basketTotals } = useBasketTotalsContext()

  // Only show loading spinner if we have no basket totals data at all
  // Don't show spinner when data exists but is just reloading (e.g., payment method changes)
  if (!basketTotals.data?.checkoutBasketTotals && basketTotals.loading) {
    return <LoadingSpinner />
  }

  // If we somehow don't have basket totals data and we're not loading, show spinner anyway
  if (!basketTotals.data?.checkoutBasketTotals) {
    return <LoadingSpinner />
  }

  return (
    <StripeElementsProvider
      amount={basketTotals.data.checkoutBasketTotals.sumTotal}
    >
      {({ isApplePayAvailable, isGooglePayAvailable }) => {
        return (
          <PaymentForm
            isApplePayAvailable={isApplePayAvailable}
            isGooglePayAvailable={isGooglePayAvailable}
          />
        )
      }}
    </StripeElementsProvider>
  )
}
const PaymentForm: React.FC<{
  isApplePayAvailable: boolean
  isGooglePayAvailable: boolean
}> = ({ isApplePayAvailable, isGooglePayAvailable }) => {
  const [showConfirmAddress, setConfirmAddress] = useState<
    SharedAddress | undefined
  >()
  const { basketTotals } = useBasketTotalsContext()
  const basketItems = useBasketItems()
  const { confirmAddressAtPayment } = useMarketplace()
  const addressHasBeenConfirmed = useRef(false)
  const paymentMethod = useReactiveVar(paymentMethodVar)
  const { createOrder, validationErrors, loading } = useCreateOrderMutation()
  const {
    data: { currentFulfilment, historicalData },
    outlet,
  } = useOutletFulfilment({ stateType: OutletFulfilmentStateType.GLOBAL })
  const { data } = useCustomerDetailsAndAddressesQuery({
    addressAcceptingOrdersToOutletId: outlet.id,
  })
  const { t } = useTranslation('checkout')

  // Track previous payment cards to detect changes
  const previousPaymentCardsRef = useRef<CardPartial[]>([])

  const isExpired = useCallback(
    (card: {
      id: string
      last4: string
      exp_year: string | null
      exp_month: string | null
      brand: string | null
    }) => {
      const cardExpiryDate = new Date(
        Number(card.exp_year),
        Number(card.exp_month) - 1
      )
      const cardExpiryEndOfMonth = endOfMonth(cardExpiryDate)
      return isAfter(new Date(), cardExpiryEndOfMonth)
    },
    []
  )

  // Fetch payment cards
  const {
    data: paymentCardData,
    loading: loadingPaymentCards,
    error: paymentCardsDataError,
    refetch: refetchPaymentCardData,
  } = usePaymentCardsQuery()

  const paymentCards = useMemo(
    () => paymentCardData?.customerDetails.cards.cards || [],
    [paymentCardData?.customerDetails.cards.cards]
  )

  // Auto-selection logic: runs when payment cards or payment method state changes
  useEffect(() => {
    const currentCards = paymentCards
    const previousCards = previousPaymentCardsRef.current

    // Only run if we have payment cards data
    if (paymentCardData?.customerDetails.cards.cards) {
      const defaultedPaymentMethod = getDefaultPaymentMethod(
        paymentCardData.customerDetails.cards.cards,
        isExpired,
        currentFulfilment
      )

      // Check for card changes (additions, removals, reordering)
      const cardsChanged =
        previousCards.length !== currentCards.length ||
        previousCards.some(
          (prevCard, index) =>
            !currentCards[index] || prevCard.id !== currentCards[index].id
        )

      // Auto-select if:
      // 1. Currently on manual confirmation without manual selection (system default)
      // 2. Current card method is invalid/expired
      // 3. Cards have changed and we need to update to next valid option
      // Don't override manual "Add new card" selections
      const shouldUpdate =
        (paymentMethod.detailedPaymentMethod ===
          DetailedPaymentMethod.MANUAL_CONFIRMATION_CARD &&
          !paymentMethod.manualSelection) ||
        (paymentMethod.detailedPaymentMethod === DetailedPaymentMethod.CARD &&
          !paymentCardData.customerDetails.cards.cards.find(
            card =>
              card.id === paymentMethod.cardPaymentToken && !isExpired(card)
          )) ||
        (cardsChanged &&
          paymentMethod.detailedPaymentMethod === DetailedPaymentMethod.CARD)

      if (shouldUpdate) {
        const updatedPaymentMethod = cardsChanged
          ? getNextValidPaymentMethod(
              currentCards,
              isExpired,
              currentFulfilment,
              paymentMethod
            )
          : defaultedPaymentMethod

        // Only update if the method actually needs to change
        const methodChanged =
          updatedPaymentMethod.detailedPaymentMethod !==
            paymentMethod.detailedPaymentMethod ||
          (updatedPaymentMethod.detailedPaymentMethod ===
            DetailedPaymentMethod.CARD &&
            paymentMethod.detailedPaymentMethod ===
              DetailedPaymentMethod.CARD &&
            'cardPaymentToken' in updatedPaymentMethod &&
            'cardPaymentToken' in paymentMethod &&
            updatedPaymentMethod.cardPaymentToken !==
              paymentMethod.cardPaymentToken)

        if (methodChanged) {
          paymentMethodVar(updatedPaymentMethod)
        }
      }

      // Update the ref for next comparison
      previousPaymentCardsRef.current = currentCards
    }
  }, [
    paymentCardData?.customerDetails.cards.cards,
    paymentCards,
    currentFulfilment,
    isExpired,
    paymentMethod,
  ])

  const addresses = data?.customerDetails.deliveryAddresses || []
  const { isMobile } = useBreakpoint()
  const { safeAreaInsetBottom } = useSafeArea()

  // Only show loading spinner on initial load when we have no payment card data at all
  if (!paymentCardData && loadingPaymentCards) {
    return <LoadingSpinner />
  }

  // will never happen as basket totals will already have data from previous steps
  if (!basketTotals.data?.checkoutBasketTotals) {
    return null
  }

  const amount = basketTotals.data?.checkoutBasketTotals.sumTotal

  const isAgeVerificationRequired =
    basketTotals.data.basketItemsWithPrices.some(
      item => item.outletMenuItem.ageRestricted
    )

  if (!currentFulfilment.availablePaymentMethods.length) {
    const fulfilmentErrorMessageMap: Record<
      NarrowFulfilmentMethodInputType,
      string
    > = {
      [NarrowFulfilmentMethodInputType.DELIVERY]: t(
        'no_payment_methods_delivery'
      ),
      [NarrowFulfilmentMethodInputType.COLLECTION]: t(
        'no_payment_methods_collection'
      ),
      [NarrowFulfilmentMethodInputType.TABLE]: t('no_payment_methods_table'),
    }
    return (
      <ErrorPage
        errorMessage={fulfilmentErrorMessageMap[currentFulfilment.narrowType]}
      />
    )
  }

  const fulfilmentInput = getCreateOrderFulfilmentInput({ currentFulfilment })
  if (!fulfilmentInput) {
    return (
      <ErrorPage
        errorMessage={t('fulfilment_unavailable', {
          narrowFulfilmentMethod: t(currentFulfilment.narrowType.toLowerCase()),
        })}
      />
    )
  }

  const initialValues = {
    addressConfirmed: showConfirmAddress,
    ageVerificationConfirmed: !isAgeVerificationRequired,
    payment: paymentMethod,
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validateOnChange={false}
        validate={values => {
          const errors: FormikErrors<typeof initialValues> = {}
          if (isAgeVerificationRequired && !values.ageVerificationConfirmed) {
            errors.ageVerificationConfirmed = t('age_verification_error')
          }
          if (
            confirmAddressAtPayment &&
            !addressHasBeenConfirmed.current &&
            currentFulfilment.location.type === LocationType.ADDRESS &&
            addresses.length > 1
          ) {
            const addressId = currentFulfilment.location.addressId
            const selectedAddress = addresses.find(
              (a: { id: string }) => a.id === addressId
            )
            if (selectedAddress) {
              errors.addressConfirmed = t('address_verification_error')
              setConfirmAddress(selectedAddress)
            }
          }
          return errors
        }}
        onSubmit={_values => {
          void createOrder({
            orderData: {
              fulfilment: fulfilmentInput,
              payment: {
                paymentMethod: paymentMethod.detailedPaymentMethod,
                ...(paymentMethod.detailedPaymentMethod ===
                  DetailedPaymentMethod.CARD && {
                  cardPaymentToken: paymentMethod.cardPaymentToken,
                }),
                ...(paymentMethod.detailedPaymentMethod ===
                  DetailedPaymentMethod.MANUAL_CONFIRMATION_CARD && {
                  saveCard: true,
                }),
              },
              orderItems: basketItemsToOrderItems(basketItems.items),
              customerOrderNotes: historicalData.orderNotes,
              discountId:
                basketTotals.data?.checkoutBasketTotals?.appliedDiscount
                  ?.discount?.id,
              voucherKey:
                basketTotals.data?.checkoutBasketTotals?.appliedDiscount
                  ?.discount?.voucher,
            },
          })
        }}
      >
        {({ submitForm, validateForm }) => {
          const paymentInfo = {
            availablePaymentMethods: currentFulfilment.availablePaymentMethods,
            paymentValue: {
              paymentMethod: paymentMethod.detailedPaymentMethod,
              ...(paymentMethod.detailedPaymentMethod ===
                DetailedPaymentMethod.CARD && {
                cardPaymentToken: paymentMethod.cardPaymentToken,
              }),
            },
            amount,
          }
          const paymentPlatforms = {
            isApplePayAvailable,
            isGooglePayAvailable,
          }
          const addressId = historicalData.addressId || undefined
          const tableId = historicalData.tableId || undefined
          const paymentCardsInfo = {
            loadingPaymentCards,
            paymentCards,
            isExpired,
            refetchPaymentCardData,
            paymentCardsDataError,
          }
          return (
            <FlexGrowForm
              $windowHeight={window.innerHeight}
              $hasSafeArea={safeAreaInsetBottom > 0}
            >
              <FlexGrowScrollContainer>
                <PaymentMethods
                  paymentInfo={paymentInfo}
                  verificationInfo={{ isAgeVerificationRequired }}
                  historicalData={{ addressId, tableId }}
                  paymentPlatforms={paymentPlatforms}
                  paymentCardsInfo={paymentCardsInfo}
                  loading={loading}
                />
              </FlexGrowScrollContainer>
              <CheckoutButtonWrapperMobile>
                {!isMobile && safeAreaInsetBottom === 0 && <Totals />}
                {paymentMethod.detailedPaymentMethod ==
                DetailedPaymentMethod.WALLET ? (
                  <ExpressCheckoutElement
                    options={{
                      buttonHeight: 55,
                      layout: { maxColumns: 1, maxRows: 1 },
                      paymentMethodOrder: ['apple-pay', 'google-pay'],
                    }}
                    onClick={async event => {
                      await validateForm().then(errors => {
                        if (Object.keys(errors).length === 0) {
                          event.resolve()
                        }
                      })
                    }}
                    onConfirm={submitForm}
                  />
                ) : (
                  <CheckoutButton
                    content={toUpper(t('pay_now'))}
                    type="submit"
                    loading={loading}
                    dataTestId="pay-now-button"
                  />
                )}
              </CheckoutButtonWrapperMobile>
              <ConfirmAddressModal
                isOpen={Boolean(showConfirmAddress)}
                address={showConfirmAddress}
                onClose={() => setConfirmAddress(undefined)}
                addressConfirmed={async () => {
                  addressHasBeenConfirmed.current = true
                  setConfirmAddress(undefined)
                  await submitForm()
                }}
              />
            </FlexGrowForm>
          )
        }}
      </Formik>
      <OutletClosedAlert message={validationErrors.outletUnavailableMessage} />{' '}
    </>
  )
}
